import { useState, useEffect, useCallback } from 'react';
import { Megaphone, Plus, Search, TrendingUp, Users, Eye, Heart, ChevronRight, Edit2, Trash2, X, UserPlus, Instagram, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { formatNumber, formatCurrency } from '@/utils/formatters';

const OBJECTIVES = ['awareness', 'engagement', 'conversions', 'sales'];
const STATUSES = ['draft', 'active', 'paused', 'completed'];

const getStatusColor = (status) => {
  switch (status) {
    case 'active':    return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    case 'draft':     return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
    case 'paused':    return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    case 'completed': return 'bg-salty-purple/10 text-salty-purple-light border-salty-purple/20';
    default:          return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  }
};

const InfluencerCampaigns = ({ userRole }) => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        ...(search && { search }),
        ...(filterStatus && { status: filterStatus }),
      };
      
      const response = await api.getInfluencerCampaigns(params);
      setCampaigns(response.data.campaigns);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getCampaignStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [fetchCampaigns, fetchStats]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.deleteInfluencerCampaign(id);
      toast.success('Campaign deleted');
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const openCampaignDetail = async (campaign) => {
    try {
      const response = await api.getInfluencerCampaign(campaign.id);
      setSelectedCampaign(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      toast.error('Failed to load campaign details');
    }
  };

  const OBJECTIVE_META = {
    awareness:   { emoji: '📣', color: 'text-sky-500',     bg: 'bg-sky-500/10 border-sky-500/20' },
    engagement:  { emoji: '💬', color: 'text-violet-500',  bg: 'bg-violet-500/10 border-violet-500/20' },
    conversions: { emoji: '⚡', color: 'text-amber-500',   bg: 'bg-amber-500/10 border-amber-500/20' },
    sales:       { emoji: '🛒', color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  };

  const spentPct = stats ? Math.min(100, ((stats.total_spent || 0) / (stats.total_budget || 1)) * 100) : 0;
  const reachPct = stats && stats.total_reach && stats.total_reach > 0
    ? Math.min(100, (stats.total_engagement / stats.total_reach) * 100)
    : 0;

  return (
    <div data-testid="influencer-campaigns-page">

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="campaign-stats">
          {/* Card 1 — Campaigns */}
          <div className="bg-salty-card border border-salty-border rounded-xl p-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-2">Total Campaigns</p>
            <p className="font-outfit font-black text-[2rem] leading-none text-salty-text-main tabular-nums mb-1">{stats.total_campaigns || 0}</p>
            <p className="text-[11px] text-salty-text-muted mb-3">
              <span className="text-emerald-500 font-bold">{stats.active_campaigns || 0} active</span>
              {' · '}{(stats.total_campaigns || 0) - (stats.active_campaigns || 0)} others
            </p>
            {/* Objective breakdown dots */}
            <div className="flex items-center gap-3 flex-wrap">
              {Object.entries(OBJECTIVE_META).map(([key, meta]) => {
                const count = campaigns.filter(c => c.objective === key).length;
                if (!count) return null;
                return (
                  <span key={key} className={`flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${meta.bg} ${meta.color}`}>
                    {meta.emoji} {key} {count}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Card 2 — Budget */}
          <div className="bg-salty-card border border-salty-border rounded-xl p-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-2">Total Budget</p>
            <p className="font-outfit font-black text-[2rem] leading-none text-salty-text-main tabular-nums mb-1">{formatCurrency(stats.total_budget || 0)}</p>
            <p className="text-[11px] text-salty-text-muted mb-3">
              {formatCurrency(stats.total_spent || 0)} spent
              {' · '}<span className={spentPct > 80 ? 'text-amber-500 font-bold' : 'text-salty-text-muted'}>{spentPct.toFixed(0)}% utilised</span>
            </p>
            <div className="h-1.5 w-full bg-salty-subtle rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-700 ${spentPct > 90 ? 'bg-rose-500' : spentPct > 70 ? 'bg-amber-500' : 'bg-salty-teal'}`}
                style={{ width: `${spentPct}%` }} />
            </div>
          </div>

          {/* Card 3 — Reach & Engagement */}
          <div className="bg-salty-card border border-salty-border rounded-xl p-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-2">Total Reach</p>
            <p className="font-outfit font-black text-[2rem] leading-none text-salty-text-main tabular-nums mb-1">{formatNumber(stats.total_reach || 0)}</p>
            <p className="text-[11px] text-salty-text-muted mb-3">
              {formatNumber(stats.total_engagement || 0)} engagements
              {reachPct > 0 && <span className="text-salty-purple font-bold ml-1">· {reachPct.toFixed(1)}% ER</span>}
            </p>
            <div className="h-1.5 w-full bg-salty-subtle rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-salty-purple transition-all duration-700"
                style={{ width: `${Math.min(reachPct * 5, 100)}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-salty-card border border-salty-border rounded-xl p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-salty-text-muted" />
            <input
              type="text"
              placeholder="Search campaigns by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-salty-bg border border-salty-border rounded-lg text-sm text-salty-text-main placeholder:text-salty-text-muted focus:outline-none focus:ring-1 focus:ring-salty-purple focus:border-salty-purple transition-colors"
              data-testid="campaign-search-input"
            />
          </div>

          {/* Status filter pills */}
          <div className="flex items-center gap-1.5 shrink-0">
            {['', ...STATUSES].map((s) => {
              const isAll = s === '';
              const active = filterStatus === s;
              return (
                <button
                  key={s || 'all'}
                  onClick={() => { setFilterStatus(s); setPage(0); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    active
                      ? 'bg-salty-purple text-white shadow-sm shadow-salty-purple/30'
                      : 'bg-salty-bg border border-salty-border text-salty-text-muted hover:border-salty-purple/40 hover:text-salty-text-main'
                  }`}
                >
                  {isAll ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              );
            })}
          </div>

          {userRole !== 'viewer' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-salty-purple hover:bg-salty-purple-light text-white text-sm font-bold rounded-lg shadow-sm shadow-salty-purple/30 transition-all shrink-0"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4" />
              Create Campaign
            </button>
          )}
        </div>
      </div>

      {/* Campaigns List */}
      <div className="space-y-2" data-testid="campaigns-list">
        {loading ? (
          <div className="bg-salty-card border border-salty-border rounded-xl p-12 text-center">
            <div className="w-8 h-8 border-2 border-salty-purple border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-salty-text-muted">Loading campaigns...</p>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-salty-card border border-salty-border rounded-xl p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-salty-purple/10 flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-7 h-7 text-salty-purple" />
            </div>
            <p className="font-bold text-salty-text-main mb-1">No campaigns yet</p>
            <p className="text-sm text-salty-text-muted mb-4">Create your first campaign to start tracking influencer performance</p>
            {userRole !== 'viewer' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-5 py-2 bg-salty-purple hover:bg-salty-purple-light text-white text-sm font-bold rounded-lg transition-all"
              >
                + Create Campaign
              </button>
            )}
          </div>
        ) : (
          campaigns.map((campaign) => {
            const obj = OBJECTIVE_META[campaign.objective] || OBJECTIVE_META.awareness;
            const reachProgress = campaign.target_reach > 0
              ? Math.min(100, Math.round((campaign.total_reach / campaign.target_reach) * 100))
              : null;
            return (
              <div
                key={campaign.id}
                className="bg-salty-card border border-salty-border rounded-xl cursor-pointer hover:border-salty-purple/40 hover:shadow-lg hover:shadow-salty-purple/8 transition-all duration-300 group relative overflow-hidden flex flex-row items-center gap-0 before:absolute before:left-0 before:top-0 before:w-[3px] before:h-0 before:bg-salty-purple before:transition-all before:duration-500 hover:before:h-full"
                onClick={() => { setSelectedCampaign(campaign); setShowDetailModal(true); }}
              >
                {/* Name + meta */}
                <div className="flex-1 min-w-0 px-5 py-4">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <h3 className="text-sm font-outfit font-bold text-salty-text-main group-hover:text-salty-purple transition-colors truncate">
                      {campaign.name}
                    </h3>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border flex-shrink-0 ${getStatusColor(campaign.status)}`}>
                      {campaign.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                      {campaign.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-[10px] text-salty-text-muted font-bold uppercase tracking-wide">
                      <Users className="w-3 h-3 text-salty-purple/60" />
                      {campaign.influencer_count || 0} influencers
                    </span>
                    <span className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${obj.bg} ${obj.color}`}>
                      {obj.emoji} {campaign.objective || 'awareness'}
                    </span>
                  </div>
                </div>

                {/* Reach progress */}
                <div className="w-48 px-4 border-l border-salty-border/40 py-4 shrink-0">
                  {reachProgress !== null ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-salty-text-muted">Reach</span>
                        <span className={`text-[10px] font-black tabular-nums ${reachProgress >= 100 ? 'text-emerald-500' : 'text-salty-purple'}`}>{reachProgress}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-salty-subtle rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ${reachProgress >= 100 ? 'bg-emerald-500' : 'bg-gradient-to-r from-salty-purple to-violet-400'}`}
                          style={{ width: `${reachProgress}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <span className="text-[10px] text-salty-text-muted/50 italic">No target set</span>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 px-6 border-l border-salty-border/40 py-4 shrink-0">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-salty-text-muted font-bold mb-0.5 flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Reach
                    </p>
                    <p className="text-sm font-mono font-bold text-salty-text-main">{formatNumber(campaign.total_reach || 0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-salty-text-muted font-bold mb-0.5 flex items-center gap-1">
                      <Heart className="w-3 h-3" /> Engage
                    </p>
                    <p className="text-sm font-mono font-bold text-salty-text-main">{formatNumber(campaign.total_engagement || 0)}</p>
                  </div>
                </div>

                {/* Budget */}
                <div className="px-6 border-l border-salty-border/40 py-4 shrink-0 min-w-[130px] text-right">
                  <p className="text-[10px] uppercase tracking-widest text-salty-text-muted font-bold mb-0.5">Budget</p>
                  <p className="text-base font-mono font-black text-salty-text-main group-hover:text-salty-purple transition-colors">
                    {formatCurrency(campaign.total_budget || campaign.budget || 0)}
                  </p>
                </div>

                {/* Actions — revealed on hover */}
                <div
                  className="flex items-center gap-1 pr-4 pl-2 opacity-0 group-hover:opacity-100 transition-all duration-200 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {userRole !== 'viewer' && (
                    <>
                      <button
                        onClick={() => { setSelectedCampaign(campaign); setShowEditModal(true); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-salty-text-muted hover:text-salty-purple hover:bg-salty-purple/10 transition-all"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(campaign.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-salty-text-muted hover:text-rose-500 hover:bg-rose-500/10 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  <div className="w-7 h-7 flex items-center justify-center bg-salty-purple/10 rounded-lg text-salty-purple">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <CampaignCreateModal
          onClose={() => setShowCreateModal(false)}
          onSave={() => { fetchCampaigns(); fetchStats(); }}
        />
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && selectedCampaign && (
        <CampaignEditModal
          campaign={selectedCampaign}
          onClose={() => { setShowEditModal(false); setSelectedCampaign(null); }}
          onSave={() => { fetchCampaigns(); fetchStats(); }}
        />
      )}

      {/* Campaign Detail Modal */}
      {showDetailModal && selectedCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => { setShowDetailModal(false); setSelectedCampaign(null); }}
          onUpdate={() => { fetchCampaigns(); fetchStats(); }}
          userRole={userRole}
        />
      )}
    </div>
  );
};

// Shared budget breakdown component used in both Create and Edit modals
const BudgetBreakdown = ({ formData, handleChange }) => {
  const subtotal = (formData.budget || 0) + (formData.agency_fee || 0);
  const gstAmount = subtotal * (formData.gst_percent || 0) / 100;
  const total = subtotal + gstAmount;

  return (
    <div className="md:col-span-2 bg-salty-bg/60 border border-salty-border rounded-xl p-4 space-y-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Budget Breakdown (₹)</p>

      {/* Line items */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 w-36 shrink-0">Campaign Budget</span>
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
            <Input
              name="budget"
              type="number"
              value={formData.budget || ''}
              onChange={handleChange}
              className="bg-salty-card border-salty-border text-white pl-7 h-8 text-sm"
              placeholder="0"
              min="0"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 w-36 shrink-0">Agency Fee</span>
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
            <Input
              name="agency_fee"
              type="number"
              value={formData.agency_fee || ''}
              onChange={handleChange}
              className="bg-salty-card border-salty-border text-white pl-7 h-8 text-sm"
              placeholder="0"
              min="0"
            />
          </div>
        </div>

        {/* Subtotal */}
        <div className="flex items-center gap-3 pt-1 border-t border-salty-border/50">
          <span className="text-xs text-slate-500 w-36 shrink-0">Subtotal</span>
          <span className="text-sm font-semibold text-salty-text-muted">
            ₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* GST */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 w-36 shrink-0">GST</span>
          <div className="flex items-center gap-2">
            <div className="relative w-24">
              <Input
                name="gst_percent"
                type="number"
                value={formData.gst_percent ?? 18}
                onChange={handleChange}
                className="bg-salty-card border-salty-border text-white pr-6 h-8 text-sm"
                placeholder="18"
                min="0"
                max="100"
                step="0.5"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
            </div>
            <span className="text-xs text-slate-500">
              = ₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Total */}
      <div className="flex items-center justify-between pt-3 mt-1 border-t border-salty-border">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Total Budget</span>
        <span className="text-lg font-black text-salty-purple tabular-nums">
          ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
};

// Campaign Create Modal
const CampaignCreateModal = ({ onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    objective: 'awareness',
    start_date: '',
    end_date: '',
    budget: 0,
    agency_fee: 0,
    gst_percent: 18,
    target_reach: 0,
    target_engagement: 0,
    products: [],
    discount_codes: [],
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    try {
      setSaving(true);
      const subtotal = (formData.budget || 0) + (formData.agency_fee || 0);
      const total_budget = subtotal * (1 + (formData.gst_percent || 0) / 100);
      await api.createInfluencerCampaign({ ...formData, total_budget: Math.round(total_budget * 100) / 100 });
      toast.success('Campaign created');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="campaign-create-modal">
      <div className="bg-salty-card border border-salty-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-4 border-b border-salty-border">
          <h2 className="text-xl font-outfit font-semibold text-white">Create Campaign</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Campaign Name *</label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="bg-salty-bg border-salty-border text-white"
              placeholder="e.g., Summer Collection Launch"
              data-testid="input-campaign-name"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white resize-none"
              rows={3}
              placeholder="Campaign description..."
              data-testid="input-campaign-description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Objective</label>
              <select
                name="objective"
                value={formData.objective}
                onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white"
                data-testid="select-objective"
              >
                {OBJECTIVES.map(obj => (
                  <option key={obj} value={obj}>{obj.charAt(0).toUpperCase() + obj.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <BudgetBreakdown formData={formData} handleChange={handleChange} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Start Date</label>
              <Input
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white"
                data-testid="input-start-date"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">End Date</label>
              <Input
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white"
                data-testid="input-end-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Target Reach</label>
              <Input
                name="target_reach"
                type="number"
                value={formData.target_reach}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white"
                placeholder="0"
                data-testid="input-target-reach"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Target Engagement</label>
              <Input
                name="target_engagement"
                type="number"
                value={formData.target_engagement}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white"
                placeholder="0"
                data-testid="input-target-engagement"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white resize-none"
              rows={2}
              placeholder="Internal notes..."
              data-testid="input-notes"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-salty-border">
            <Button type="button" variant="outline" onClick={onClose} className="border-salty-border text-slate-300">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-salty-purple hover:bg-salty-purple-light"
              data-testid="save-campaign-btn"
            >
              {saving ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Campaign Edit Modal
const CampaignEditModal = ({ campaign, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: campaign.name || '',
    description: campaign.description || '',
    objective: campaign.objective || 'awareness',
    start_date: campaign.start_date || '',
    end_date: campaign.end_date || '',
    budget: campaign.budget || 0,
    agency_fee: campaign.agency_fee || 0,
    gst_percent: campaign.gst_percent ?? 18,
    target_reach: campaign.target_reach || 0,
    target_engagement: campaign.target_engagement || 0,
    status: campaign.status || 'draft',
    notes: campaign.notes || '',
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    try {
      setSaving(true);
      const subtotal = (formData.budget || 0) + (formData.agency_fee || 0);
      const total_budget = subtotal * (1 + (formData.gst_percent || 0) / 100);
      await api.updateInfluencerCampaign(campaign.id, { ...formData, total_budget: Math.round(total_budget * 100) / 100 });
      toast.success('Campaign updated');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="campaign-edit-modal">
      <div className="bg-salty-card border border-salty-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-salty-border">
          <h2 className="text-xl font-outfit font-bold text-white tracking-tight">Edit Campaign</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">Campaign Name *</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white focus:border-salty-purple/50"
                placeholder="e.g., Summer Collection Launch"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-xl px-3 py-2 text-white resize-none text-sm focus:border-salty-purple/50 focus:ring-1 focus:ring-salty-purple/20 outline-none transition-all"
                rows={3}
                placeholder="Campaign description..."
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-xl px-3 py-2 text-white text-sm focus:border-salty-purple/50 focus:ring-1 focus:ring-salty-purple/20 outline-none transition-all"
              >
                {STATUSES.map(status => (
                  <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">Objective</label>
              <select
                name="objective"
                value={formData.objective}
                onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-xl px-3 py-2 text-white text-sm focus:border-salty-purple/50 focus:ring-1 focus:ring-salty-purple/20 outline-none transition-all"
              >
                {OBJECTIVES.map(obj => (
                  <option key={obj} value={obj}>{obj.charAt(0).toUpperCase() + obj.slice(1)}</option>
                ))}
              </select>
            </div>

            <BudgetBreakdown formData={formData} handleChange={handleChange} />

            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">Target Reach</label>
              <Input
                name="target_reach"
                type="number"
                value={formData.target_reach}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white focus:border-salty-purple/50"
                placeholder="0"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">Start Date</label>
              <Input
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white focus:border-salty-purple/50"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 block font-bold">End Date</label>
              <Input
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white focus:border-salty-purple/50"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-salty-border">
            <Button type="button" variant="outline" onClick={onClose} className="border-salty-border text-slate-300 hover:bg-salty-bg/50">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-salty-purple hover:bg-salty-purple-light text-white font-bold px-8 shadow-lg shadow-salty-purple/20"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Campaign Detail Modal
const CampaignDetailModal = ({ campaign, onClose, onUpdate, userRole }) => {
  const [influencers, setInfluencers] = useState([]);
  const [availableInfluencers, setAvailableInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddInfluencer, setShowAddInfluencer] = useState(false);
  const [addingInfluencer, setAddingInfluencer] = useState(null);
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [selectedInfluencers, setSelectedInfluencers] = useState([]);

  const fetchCampaignInfluencers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getCampaignInfluencers(campaign.id);
      setInfluencers(response.data);
    } catch (error) {
      console.error('Error fetching campaign influencers:', error);
    } finally {
      setLoading(false);
    }
  }, [campaign.id]);

  useEffect(() => {
    fetchCampaignInfluencers();
  }, [fetchCampaignInfluencers]);

  const fetchAvailableInfluencers = async () => {
    try {
      const response = await api.getInfluencers({ limit: 100, status: 'active' });
      // Filter out already added influencers
      const campaignInfluencerIds = influencers.map(i => i.influencer_id);
      setAvailableInfluencers(
        response.data.influencers.filter(i => !campaignInfluencerIds.includes(i.id))
      );
    } catch (error) {
      console.error('Error fetching available influencers:', error);
    }
  };

  const handleAddInfluencer = async (influencerId) => {
    try {
      setAddingInfluencer(influencerId);
      await api.addInfluencerToCampaign(campaign.id, { influencer_id: influencerId });
      toast.success('Influencer added to campaign');
      fetchCampaignInfluencers();
      onUpdate();
      setShowAddInfluencer(false);
    } catch (error) {
      console.error('Error adding influencer:', error);
      toast.error('Failed to add influencer');
    } finally {
      setAddingInfluencer(null);
    }
  };

  const handleBulkAdd = async () => {
    if (selectedInfluencers.length === 0) return;
    
    try {
      setAddingInfluencer('bulk');
      await api.bulkAddInfluencersToCampaign(campaign.id, selectedInfluencers);
      toast.success(`${selectedInfluencers.length} influencers added to campaign`);
      setSelectedInfluencers([]);
      setInfluencerSearch('');
      fetchCampaignInfluencers();
      onUpdate();
      setShowAddInfluencer(false);
    } catch (error) {
      console.error('Error bulk adding influencers:', error);
      toast.error('Failed to add influencers');
    } finally {
      setAddingInfluencer(null);
    }
  };

  const toggleInfluencerSelection = (id) => {
    setSelectedInfluencers(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredAvailableInfluencers = availableInfluencers.filter(inf => 
    inf.name?.toLowerCase().includes(influencerSearch.toLowerCase()) || 
    inf.instagram_handle?.toLowerCase().includes(influencerSearch.toLowerCase())
  );

  const handleRemoveInfluencer = async (influencerId) => {
    if (!window.confirm('Remove this influencer from the campaign?')) return;
    
    try {
      await api.removeInfluencerFromCampaign(campaign.id, influencerId);
      toast.success('Influencer removed');
      fetchCampaignInfluencers();
      onUpdate();
    } catch (error) {
      console.error('Error removing influencer:', error);
      toast.error('Failed to remove influencer');
    }
  };

  const [syncing, setSyncing] = useState(false);
  const [apiCredits, setApiCredits] = useState(null);

  const fetchApiCredits = async () => {
    try {
      const res = await api.getApiCredits();
      setApiCredits(res.data.credits || []);
    } catch (_) {}
  };

  useEffect(() => { fetchApiCredits(); }, []);

  const handleSyncPerformance = async () => {
    try {
      setSyncing(true);
      // Start background sync — returns immediately with a job_id
      const startRes = await api.syncCampaignPerformance(campaign.id);
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job ID returned from server');

      toast.info('Sync started — this may take a minute for large campaigns…');

      // Poll every 5 seconds until done (max 3 minutes)
      const deadline = Date.now() + 3 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 5000));
        const statusRes = await api.getCampaignSyncStatus(campaign.id, jobId);
        const { status, result, error } = statusRes.data;
        if (status === 'done') {
          toast.success(`Sync complete — ${result?.synced_posts ?? 0} posts updated`);
          fetchCampaignInfluencers();
          fetchApiCredits();
          onUpdate();
          return;
        }
        if (status === 'error') {
          throw new Error(error || 'Sync failed on server');
        }
        // still running — keep polling
      }
      toast.warning('Sync is taking longer than expected — refresh in a minute to see results');
    } catch (error) {
      console.error('Error syncing performance:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Sync failed: ${detail}`);
    } finally {
      setSyncing(false);
    }
  };

  const creditWarning = apiCredits?.some(c => c.exhausted || (c.remaining != null && c.remaining < 10));
  const creditExhausted = apiCredits?.some(c => c.exhausted);
  const creditInfo = apiCredits?.map(c => {
    const label = c.api === 'scraper2' ? 'Scraper2' : 'Instagram120';
    if (c.exhausted) return `${label}: exhausted`;
    if (c.remaining != null) return `${label}: ${c.remaining} left`;
    return null;
  }).filter(Boolean).join(' · ');

  const totalAgreedFees = influencers.reduce((s, i) => s + (i.agreed_fee || 0), 0);
  const withPosts      = influencers.filter(i => (i.posts || []).length > 0).length;
  const totalViews     = influencers.reduce((s, i) => s + (i.views || 0), 0);
  const spentPct       = campaign.budget > 0 ? Math.min(100, ((campaign.budget_spent || 0) / campaign.budget) * 100) : 0;
  const reachPct       = campaign.target_reach > 0 ? Math.min(100, Math.round((campaign.total_reach / campaign.target_reach) * 100)) : null;
  // Cost per View = total spent ÷ total views (campaign level)
  const campaignCpv    = totalViews > 0 && (campaign.budget_spent || 0) > 0
    ? (campaign.budget_spent / totalViews)
    : null;

  const OBJECTIVE_META = {
    awareness:   { emoji: '📣', color: 'text-sky-500',     bg: 'bg-sky-500/10 border-sky-500/20' },
    engagement:  { emoji: '💬', color: 'text-violet-500',  bg: 'bg-violet-500/10 border-violet-500/20' },
    conversions: { emoji: '⚡', color: 'text-amber-500',   bg: 'bg-amber-500/10 border-amber-500/20' },
    sales:       { emoji: '🛒', color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  };
  const obj = OBJECTIVE_META[campaign.objective] || OBJECTIVE_META.awareness;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="campaign-detail-modal">
      <div className="bg-salty-card border border-salty-border rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col m-4 relative overflow-hidden shadow-2xl shadow-black/40">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-salty-border bg-salty-card shrink-0">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-salty-purple/15 flex items-center justify-center shrink-0 mt-0.5">
              <Megaphone className="w-5 h-5 text-salty-purple" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <h2 className="text-lg font-outfit font-bold text-salty-text-main tracking-tight">{campaign.name}</h2>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border flex-shrink-0 ${getStatusColor(campaign.status)}`}>
                  {campaign.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                  {campaign.status}
                </span>
                <span className={`flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${obj.bg} ${obj.color}`}>
                  {obj.emoji} {campaign.objective}
                </span>
              </div>
              <p className="text-xs text-salty-text-muted">
                {influencers.length} influencers · {withPosts} with posts · {formatCurrency(totalAgreedFees)} committed
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-salty-text-muted hover:text-salty-text-main hover:bg-salty-subtle transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 custom-scrollbar">
          {/* KPI Strip */}
          <div className="grid grid-cols-5 border-b border-salty-border divide-x divide-salty-border">
            {/* Budget */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-1">Budget</p>
              <p className="font-outfit font-black text-xl text-salty-text-main tabular-nums">{formatCurrency(campaign.budget || 0)}</p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-salty-text-muted">{formatCurrency(campaign.budget_spent || 0)} spent</span>
                  <span className={spentPct > 80 ? 'text-amber-500 font-bold' : 'text-salty-text-muted'}>{spentPct.toFixed(0)}%</span>
                </div>
                <div className="h-1 w-full bg-salty-subtle rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${spentPct > 90 ? 'bg-rose-500' : spentPct > 70 ? 'bg-amber-500' : 'bg-salty-teal'}`}
                    style={{ width: `${spentPct}%` }} />
                </div>
              </div>
            </div>

            {/* Reach */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-1">Reach</p>
              <p className="font-outfit font-black text-xl text-salty-text-main tabular-nums">{formatNumber(campaign.total_reach || 0)}</p>
              {reachPct !== null ? (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-salty-text-muted">Target {formatNumber(campaign.target_reach)}</span>
                    <span className={`font-bold ${reachPct >= 100 ? 'text-emerald-500' : 'text-salty-purple'}`}>{reachPct}%</span>
                  </div>
                  <div className="h-1 w-full bg-salty-subtle rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${reachPct >= 100 ? 'bg-emerald-500' : 'bg-salty-purple'}`}
                      style={{ width: `${reachPct}%` }} />
                  </div>
                </div>
              ) : (
                <p className="text-[10px] text-salty-text-muted mt-2 italic">No target set</p>
              )}
            </div>

            {/* Engagement */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-1">Engagement</p>
              <p className="font-outfit font-black text-xl text-salty-text-main tabular-nums">{formatNumber(campaign.total_engagement || 0)}</p>
              <p className="text-[10px] text-salty-text-muted mt-2">
                {campaign.total_reach > 0
                  ? `${((campaign.total_engagement / campaign.total_reach) * 100).toFixed(1)}% ER`
                  : 'No reach data yet'}
              </p>
            </div>

            {/* Total Views (from posts) */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-1">Views (Posts)</p>
              <p className="font-outfit font-black text-xl text-salty-text-main tabular-nums">{formatNumber(totalViews)}</p>
              <p className="text-[10px] text-salty-text-muted mt-2">
                {withPosts} of {influencers.length} have posts
                {withPosts < influencers.length && <span className="text-amber-500 font-bold"> · {influencers.length - withPosts} pending</span>}
              </p>
            </div>

            {/* Cost per View */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted mb-1 flex items-center gap-1">
                Cost / View
              </p>
              {campaignCpv !== null ? (
                <>
                  <p className={`font-outfit font-black text-xl tabular-nums ${
                    campaignCpv < 0.5 ? 'text-emerald-500' :
                    campaignCpv < 2   ? 'text-salty-text-main' :
                    'text-amber-500'
                  }`}>
                    ₹{campaignCpv < 1 ? campaignCpv.toFixed(2) : campaignCpv.toFixed(1)}
                  </p>
                  <p className="text-[10px] text-salty-text-muted mt-2">
                    {campaignCpv < 0.5
                      ? '🟢 Excellent efficiency'
                      : campaignCpv < 2
                      ? '🟡 Good CPV'
                      : '🔴 High CPV'}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-outfit font-black text-xl text-salty-text-muted/40">—</p>
                  <p className="text-[10px] text-salty-text-muted mt-2 italic">
                    {totalViews === 0 ? 'Sync views first' : 'No spend recorded'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Influencers section */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-bold text-salty-text-main flex items-center gap-2">
                <Users className="w-4 h-4 text-salty-purple" />
                Campaign Influencers
                <span className="text-xs bg-salty-purple/15 text-salty-purple px-2 py-0.5 rounded-full font-bold">{influencers.length}</span>
              </h3>
              <div className="flex items-center gap-2">
                {/* API credit indicator */}
                {apiCredits && apiCredits.length > 0 && (
                  <span
                    title={creditInfo || 'API credits OK'}
                    className={`text-[10px] font-semibold px-2 py-1 rounded-lg border cursor-help transition-all ${
                      creditExhausted
                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                        : creditWarning
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                        : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    }`}
                  >
                    {creditExhausted ? '⚠ Credits exhausted' : creditWarning ? '⚠ Credits low' : '✓ API OK'}
                  </span>
                )}
                 {userRole !== 'viewer' && (
                   <>
                     <button
                       onClick={handleSyncPerformance}
                       disabled={syncing || loading || influencers.length === 0}
                       className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold border border-salty-border rounded-lg text-salty-text-muted hover:text-salty-text-main hover:border-salty-purple/40 transition-all disabled:opacity-40"
                     >
                       <TrendingUp className={`w-3.5 h-3.5 ${syncing ? 'animate-pulse text-salty-purple' : ''}`} />
                       {syncing ? 'Syncing…' : 'Sync Views'}
                     </button>
                     <button
                       onClick={() => { setShowAddInfluencer(true); setInfluencerSearch(''); setSelectedInfluencers([]); fetchAvailableInfluencers(); }}
                       className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold bg-salty-purple hover:bg-salty-purple-light text-white rounded-lg shadow-sm shadow-salty-purple/30 transition-all"
                       data-testid="add-influencer-btn"
                     >
                       <UserPlus className="w-3.5 h-3.5" />
                       Add Influencer
                     </button>
                   </>
                 )}
               </div>
             </div>
 
             {loading ? (
               <div className="flex flex-col items-center justify-center py-14 gap-3">
                 <div className="w-8 h-8 border-2 border-salty-purple border-t-transparent rounded-full animate-spin" />
                 <p className="text-sm text-salty-text-muted">Loading influencers…</p>
               </div>
             ) : influencers.length === 0 ? (
               <div className="text-center py-14 border border-dashed border-salty-border rounded-xl">
                 <div className="w-12 h-12 rounded-2xl bg-salty-purple/10 flex items-center justify-center mx-auto mb-3">
                   <Users className="w-6 h-6 text-salty-purple/60" />
                 </div>
                 <p className="font-bold text-salty-text-main mb-1">No influencers yet</p>
                 <p className="text-xs text-salty-text-muted mb-4">Add influencers to start tracking performance</p>
                 {userRole !== 'viewer' && (
                   <button
                     onClick={() => { setShowAddInfluencer(true); setInfluencerSearch(''); setSelectedInfluencers([]); fetchAvailableInfluencers(); }}
                     className="px-4 py-2 text-xs font-bold bg-salty-purple hover:bg-salty-purple-light text-white rounded-lg transition-all"
                   >
                     + Add Influencer
                   </button>
                 )}
               </div>
            ) : (
              <div className="rounded-xl border border-salty-border/60 overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[minmax(180px,1fr)_100px_75px_120px_120px_110px_110px_40px] bg-salty-bg/60 border-b border-salty-border/60 px-6 py-3 gap-x-6">
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted">Influencer</span>
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted text-right">Fee</span>
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted text-center">Posts</span>
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted text-right">Views</span>
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted text-right">Cost/View</span>
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted text-right">Likes</span>
                  <span className="text-[10px] uppercase tracking-widest font-black text-salty-text-muted text-right">Comments</span>
                  <span />
                </div>
                {/* Rows */}
                {influencers.map((inf, i) => (
                  <CampaignInfluencerRow
                    key={inf.id}
                    inf={inf}
                    campaignId={campaign.id}
                    isLast={i === influencers.length - 1}
                    onUpdate={() => { fetchCampaignInfluencers(); onUpdate(); }}
                    onRemove={() => handleRemoveInfluencer(inf.influencer_id)}
                    userRole={userRole}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add Influencer Slide-out */}
        {showAddInfluencer && (
          <div className="absolute inset-0 z-20 flex justify-end">
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" 
              onClick={() => setShowAddInfluencer(false)}
            />
            <div className="relative w-full max-w-sm bg-salty-card border-l border-salty-border shadow-2xl flex flex-col h-full animate-in slide-in-from-right duration-300">
              <div className="p-4 border-b border-salty-border bg-salty-bg/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-outfit font-bold text-white flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-salty-purple" />
                    Add Influencer
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowAddInfluencer(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={influencerSearch}
                    onChange={(e) => setInfluencerSearch(e.target.value)}
                    placeholder="Search by name or handle..."
                    className="h-9 bg-salty-bg border-salty-border text-xs pl-9 focus:border-salty-purple/50"
                    autoFocus
                  />
                </div>
              </div>
              
              <div className="p-4 flex-1 overflow-y-auto custom-scrollbar space-y-3">
                {filteredAvailableInfluencers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-center">
                    <Search className="w-8 h-8 mb-2 opacity-20" />
                    <p className="text-sm">No influencers match your search</p>
                    {influencerSearch && (
                      <Button variant="link" size="sm" onClick={() => setInfluencerSearch('')} className="text-salty-purple text-xs h-auto p-0 mt-1">
                        Clear search
                      </Button>
                    )}
                  </div>
                ) : (
                  filteredAvailableInfluencers.map((inf) => (
                    <div
                      key={inf.id}
                      className={`flex items-center justify-between p-3 border rounded-xl group transition-all cursor-pointer ${selectedInfluencers.includes(inf.id) ? 'bg-salty-purple/10 border-salty-purple/50' : 'bg-salty-bg/40 border-salty-border/50 hover:border-salty-purple/30'}`}
                      onClick={() => toggleInfluencerSelection(inf.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full bg-cover bg-center border border-salty-border/50 shadow-sm"
                          style={inf.profile_pic ? { backgroundImage: `url(https://images.weserv.nl/?url=${encodeURIComponent(inf.profile_pic)}&w=100&h=100&fit=cover)` } : { backgroundColor: 'var(--salty-purple-darker)' }}
                        >
                          {!inf.profile_pic && (
                            <div className="w-full h-full flex items-center justify-center text-xs font-bold text-salty-purple-light">
                              {inf.name?.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <p className="text-white font-semibold text-sm group-hover:text-salty-purple-light transition-colors line-clamp-1">{inf.name}</p>
                          <p className="text-[10px] text-slate-500 font-medium">
                            {formatNumber(inf.followers_count || 0)} followers
                          </p>
                        </div>
                      </div>
                      
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${selectedInfluencers.includes(inf.id) ? 'bg-salty-purple border-salty-purple text-white shadow-lg shadow-salty-purple/30' : 'border-salty-border group-hover:border-salty-purple/50'}`}>
                        {selectedInfluencers.includes(inf.id) && <Check className="w-3 h-3" />}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {selectedInfluencers.length > 0 && (
                <div className="p-4 border-t border-salty-border bg-salty-bg/80 backdrop-blur-sm animate-in slide-in-from-bottom-2 duration-300">
                  <Button
                    className="w-full bg-salty-purple hover:bg-salty-purple-light font-bold text-sm shadow-xl shadow-salty-purple/20"
                    onClick={handleBulkAdd}
                    disabled={addingInfluencer === 'bulk'}
                  >
                    {addingInfluencer === 'bulk' ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                        Adding...
                      </div>
                    ) : (
                      `Add ${selectedInfluencers.length} Influencer${selectedInfluencers.length > 1 ? 's' : ''}`
                    )}
                  </Button>
                  <p className="text-[10px] text-slate-500 text-center mt-2">
                    Multiple influencers will be added in one batch
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CampaignInfluencerRow = ({ inf, campaignId, onUpdate, onRemove, isLast, userRole }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showPosts, setShowPosts] = useState(false);
  const [newPostUrl, setNewPostUrl] = useState('');
  const [pendingUrls, setPendingUrls] = useState([]);
  const [agreedFee, setAgreedFee] = useState(inf.agreed_fee || 0);
  const [saving, setSaving] = useState(false);

  const handleUpdate = async () => {
    try {
      setSaving(true);
      await api.updateCampaignInfluencer(campaignId, inf.influencer_id, {
        agreed_fee: agreedFee,
        posts: inf.posts
      });
      toast.success('Influencer updated');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating influencer:', error);
      toast.error('Failed to update influencer');
    } finally {
      setSaving(false);
    }
  };

  const handleStageUrl = () => {
    const url = newPostUrl.trim();
    if (!url) return;
    if (!url.includes('instagram.com/')) {
      toast.error('Please enter a valid Instagram URL');
      return;
    }
    if (pendingUrls.includes(url) || (inf.posts || []).some(p => p.url === url)) {
      toast.error('This URL is already added');
      return;
    }
    setPendingUrls(prev => [...prev, url]);
    setNewPostUrl('');
  };

  const handleSavePosts = async () => {
    if (pendingUrls.length === 0) return;
    try {
      setSaving(true);
      const newPosts = pendingUrls.map(url => ({ url, type: 'reel', views: 0, likes: 0, comments: 0 }));
      const updatedPosts = [...(inf.posts || []), ...newPosts];
      await api.updateCampaignInfluencer(campaignId, inf.influencer_id, { posts: updatedPosts });
      setPendingUrls([]);
      toast.success(`${newPosts.length} post${newPosts.length > 1 ? 's' : ''} saved`);
      onUpdate();
    } catch (error) {
      console.error('Error saving posts:', error);
      toast.error('Failed to save posts');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePost = async (index) => {
    try {
      const updatedPosts = [...(inf.posts || [])];
      updatedPosts.splice(index, 1);
      await api.updateCampaignInfluencer(campaignId, inf.influencer_id, {
        posts: updatedPosts
      });
      toast.success('Post removed');
      onUpdate();
    } catch (error) {
      console.error('Error removing post:', error);
      toast.error('Failed to remove post');
    }
  };

  const hasPosts        = (inf.posts || []).length > 0;
  const viewsOk         = (inf.views || 0) > 0;
  const likesOk         = (inf.likes || 0) > 0;
  // Cost per View: agreed fee ÷ views
  const cpv = viewsOk && (inf.agreed_fee || 0) > 0
    ? (inf.agreed_fee / inf.views)
    : null;
  const isSynced        = hasPosts && (inf.posts || []).some(p => p.synced_at);
  const viewsUnavail    = isSynced && !viewsOk && (inf.posts || []).some(p => p.views_source === 'unavailable');
  const viewsHistorical = isSynced && viewsOk && (inf.posts || []).some(p => p.views_source === 'historical');
  // views_synced_at = when the view count was actually fetched (may be older than last sync)
  const viewsSyncedAt   = (inf.posts || []).find(p => p.views_synced_at)?.views_synced_at;
  const viewsSyncDate   = viewsSyncedAt
    ? new Date(viewsSyncedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : null;
  // Old stale data: views == likes (pre-fix substitution)
  const viewsStale      = viewsOk && likesOk && inf.views === inf.likes && !viewsHistorical;

  // Views display helper
  const viewsDisplay = viewsStale ? { label: 'Sync again', type: 'warn' }
    : viewsHistorical ? { label: formatNumber(inf.views), sub: `as of ${viewsSyncDate || '—'} ⓘ`, title: `Live data unavailable · Last fetched ${viewsSyncDate || 'previously'}`, type: 'historical' }
    : viewsOk         ? { label: formatNumber(inf.views), type: 'ok' }
    : viewsUnavail    ? { label: 'N/A', title: "Instagram doesn't expose view count for posts older than the recent feed", type: 'na' }
    : hasPosts        ? { label: 'Sync needed', type: 'warn' }
    : { label: '—', type: 'empty' };

  return (
    <div className={`group/row hover:bg-salty-purple/[0.02] transition-colors ${!isLast ? 'border-b border-salty-border/40' : ''}`}>

      {/* Main row — same 7-column grid as the header */}
      <div className="grid grid-cols-[minmax(180px,1fr)_100px_75px_120px_120px_110px_110px_40px] items-center px-6 py-3.5 gap-x-4">

        {/* Influencer identity */}
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-full shrink-0 bg-cover bg-center border-2 border-salty-border/60 group-hover/row:border-salty-purple/30 transition-all flex items-center justify-center text-salty-purple font-bold text-sm overflow-hidden"
            style={inf.influencer_profile_pic
              ? { backgroundImage: `url(https://images.weserv.nl/?url=${encodeURIComponent(inf.influencer_profile_pic)}&w=80&h=80&fit=cover)` }
              : { backgroundColor: 'var(--salty-purple-darker)' }}
          >
            {!inf.influencer_profile_pic && (inf.influencer_name?.charAt(0)?.toUpperCase() || '?')}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-salty-text-main leading-snug truncate">{inf.influencer_name}</p>
            <p className="text-[11px] text-salty-text-muted mt-0.5 truncate">
              @{inf.influencer_handle}
              {inf.influencer_followers > 0 && <span className="ml-1.5 opacity-60">{formatNumber(inf.influencer_followers)}</span>}
            </p>
          </div>
        </div>

        {/* Fee */}
        <div className="text-right">
          {isEditing ? (
            <div className="flex items-center gap-1 justify-end">
              <input
                type="number"
                value={agreedFee}
                onChange={(e) => setAgreedFee(parseFloat(e.target.value) || 0)}
                className="h-6 w-16 bg-salty-bg border border-salty-purple/50 rounded px-1 text-xs font-bold focus:outline-none text-right"
                autoFocus
              />
              <button onClick={handleUpdate} disabled={saving} className="w-5 h-5 flex items-center justify-center rounded bg-emerald-500/10 text-emerald-500 shrink-0">
                <Check className="w-3 h-3" />
              </button>
            </div>
          ) : userRole === 'viewer' ? (
            <div className="flex items-center justify-end w-full">
              <span className="text-[15px] font-semibold text-salty-text-main tabular-nums">{formatCurrency(inf.agreed_fee || 0)}</span>
            </div>
          ) : (
            <button onClick={() => setIsEditing(true)} className="group/fee flex items-center gap-0.5 justify-end w-full">
              <span className="text-[15px] font-semibold text-salty-text-main tabular-nums">{formatCurrency(inf.agreed_fee || 0)}</span>
              <Edit2 className="w-2.5 h-2.5 text-salty-text-muted opacity-0 group-hover/fee:opacity-60 transition-opacity" />
            </button>
          )}
        </div>

        {/* Posts */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowPosts(!showPosts)}
            className={`flex items-center gap-0.5 text-[15px] font-semibold rounded px-2 py-1 transition-all ${
              showPosts ? 'bg-salty-purple/15 text-salty-purple'
              : hasPosts ? 'text-salty-text-main hover:bg-salty-subtle'
              : 'text-amber-500 hover:bg-amber-500/10'
            }`}
          >
            {hasPosts ? (inf.posts || []).length : '0'}
            <Plus className={`w-3 h-3 transition-transform duration-200 ${showPosts ? 'rotate-45' : ''}`} />
          </button>
        </div>

        {/* Views */}
        <div className="text-right">
          {viewsDisplay.type === 'warn' && (
            <span className="text-[10px] text-amber-500 font-medium">{viewsDisplay.label}</span>
          )}
          {viewsDisplay.type === 'historical' && (
            <div title={viewsDisplay.title} className="cursor-help">
              <p className="text-[15px] font-semibold text-salty-text-main tabular-nums">{viewsDisplay.label}</p>
              <p className="text-[10px] text-salty-text-muted/60">{viewsDisplay.sub}</p>
            </div>
          )}
          {viewsDisplay.type === 'ok' && (
            <span className="text-[15px] font-semibold text-salty-text-main tabular-nums">{viewsDisplay.label}</span>
          )}
          {viewsDisplay.type === 'na' && (
            <span title={viewsDisplay.title} className="text-xs text-salty-text-muted/60 cursor-help">N/A ⓘ</span>
          )}
          {viewsDisplay.type === 'empty' && (
            <span className="text-salty-text-muted/40">—</span>
          )}
        </div>

        {/* Cost / View */}
        <div className="text-right">
          {cpv !== null ? (
            <>
              <p className={`text-[15px] font-bold tabular-nums ${cpv < 0.5 ? 'text-emerald-500' : cpv < 2 ? 'text-salty-text-main' : 'text-amber-500'}`}>
                ₹{cpv < 1 ? cpv.toFixed(2) : cpv.toFixed(1)}
              </p>
              <p className={`text-[10px] font-medium ${cpv < 0.5 ? 'text-emerald-500/70' : cpv < 2 ? 'text-salty-text-muted/50' : 'text-amber-500/70'}`}>
                {cpv < 0.5 ? '↑ great' : cpv < 2 ? 'good' : '↓ high'}
              </p>
            </>
          ) : (
            <span className="text-salty-text-muted/40">—</span>
          )}
        </div>

        {/* Likes */}
        <div className="text-right">
          <p className="text-[15px] font-semibold text-salty-text-main tabular-nums">
            {likesOk ? formatNumber(inf.likes) : '—'}
          </p>
        </div>

        {/* Comments */}
        <div className="text-right">
          <p className="text-[15px] font-semibold text-salty-text-main tabular-nums">
            {(inf.comments || 0) > 0 ? formatNumber(inf.comments) : '—'}
          </p>
        </div>

        {/* Delete */}
        {userRole !== 'viewer' ? (
          <button
            onClick={onRemove}
            className="w-7 h-7 flex items-center justify-center rounded text-salty-text-muted/20 hover:text-rose-500 hover:bg-rose-500/10 transition-all"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="w-7" />
        )}
      </div>

      {showPosts && (
        <div className="px-4 pb-4 pt-0 animate-in slide-in-from-top-2 duration-300">
          <div className="space-y-3 mb-4 mt-2">
            {(inf.posts || []).length === 0 ? (
              <div className="py-6 text-center border border-dashed border-salty-border rounded-xl bg-salty-bg/10">
                <p className="text-xs text-slate-500">No post URLs added yet</p>
              </div>
            ) : (
              (inf.posts || []).map((post, idx) => (
                <div key={idx} className="flex items-center justify-between bg-salty-bg/50 border border-salty-border/50 rounded-xl px-4 py-3 text-sm group/post hover:border-salty-purple/20">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Instagram className="w-3 h-3 text-pink-500" />
                      <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-salty-purple-light hover:underline truncate block font-medium">
                        {post.url}
                      </a>
                    </div>
                    <div className="flex items-center gap-4 text-[11px]">
                      <span className="flex items-center gap-1.5 text-slate-300 bg-salty-bg px-2 py-0.5 rounded border border-salty-border/30">
                        <Eye className="w-3.5 h-3.5 text-amber-500" /> 
                        <span className="font-bold">{formatNumber(post.views || 0)}</span>
                        <span className="text-[9px] opacity-60 font-medium">VIEWS</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-300 bg-salty-bg px-2 py-0.5 rounded border border-salty-border/30">
                        <Heart className="w-3.5 h-3.5 text-rose-500" /> 
                        <span className="font-bold">{formatNumber(post.likes || 0)}</span>
                        <span className="text-[9px] opacity-60 font-medium">LIKES</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-300 bg-salty-bg px-2 py-0.5 rounded border border-salty-border/30">
                        <Plus className="w-3.5 h-3.5 text-blue-500" /> 
                        <span className="font-bold">{formatNumber(post.comments || 0)}</span>
                        <span className="text-[9px] opacity-60 font-medium">CMNTS</span>
                      </span>
                    </div>
                  </div>
                  {userRole !== 'viewer' && (
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-8 w-8 text-slate-500 hover:text-salty-rose hover:bg-salty-rose/10 opacity-0 group-hover/post:opacity-100 transition-all rounded-lg"
                      onClick={() => handleRemovePost(idx)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))
             )}
          </div>

          {/* Pending URLs — staged but not yet saved */}
          {pendingUrls.length > 0 && (
            <div className="flex flex-col gap-1.5 px-1">
              {pendingUrls.map((url, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-salty-purple/5 border border-salty-purple/20 rounded-lg">
                  <Instagram className="w-3.5 h-3.5 text-salty-purple shrink-0" />
                  <span className="text-xs text-salty-text-main truncate flex-1">{url}</span>
                  {userRole !== 'viewer' && (
                    <button
                      onClick={() => setPendingUrls(prev => prev.filter((_, j) => j !== i))}
                      className="text-slate-400 hover:text-salty-rose transition-colors shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* URL input row */}
          {userRole !== 'viewer' && (
            <div className="flex gap-2 p-3 bg-salty-bg/40 rounded-xl border border-salty-border/50 shadow-inner">
              <div className="relative flex-1">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={newPostUrl}
                  onChange={(e) => setNewPostUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleStageUrl()}
                  placeholder="Paste Instagram Reel or Post URL here..."
                  className="h-10 bg-salty-bg border-salty-border/50 text-sm flex-1 pl-10 focus:border-salty-purple focus:ring-1 focus:ring-salty-purple transition-all"
                />
              </div>
              <Button
                size="sm"
                onClick={handleStageUrl}
                disabled={!newPostUrl.trim()}
                variant="outline"
                className="h-10 px-4 border-salty-border text-sm font-semibold text-salty-text-main hover:border-salty-purple hover:text-salty-purple transition-all"
              >
                + Add
              </Button>
            </div>
          )}

          {/* Save button — only shown when there are pending URLs */}
          {pendingUrls.length > 0 && userRole !== 'viewer' && (
            <Button
              onClick={handleSavePosts}
              disabled={saving}
              className="w-full h-10 bg-salty-purple hover:bg-salty-purple-light text-sm font-bold shadow-lg shadow-salty-purple/20 transition-all"
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white" />
                  Saving…
                </div>
              ) : (
                `Save ${pendingUrls.length} Post${pendingUrls.length > 1 ? 's' : ''}`
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default InfluencerCampaigns;
